import type * as React from 'react'

import { styled } from 'styled-components'

import { ErrorBoundary } from '@b-stock/fe-core/bstock-react'
import { Breakpoints } from '@b-stock/fe-core/design-system'

import { PageWidth } from '@/components/PageWidth'
import { useLogError } from '@/helpers/useLogError'

import { Footer } from '../shared/Footer'
import { Header } from '../shared/Header'

const Body = styled.div`
  overflow-x: hidden;
`

const ContentContainer = styled(PageWidth)`
  @media ${Breakpoints.min.medium} {
    padding-top: 1.5rem;
  }

  @media ${Breakpoints.min.large} {
    margin-bottom: 2rem;
  }
`

const FooterContainer = styled.div`
  @media ${Breakpoints.min.medium} {
    padding-top: 1.5rem;
  }
`

/**
 * Wraps page content with the standard header/footer and general layout
 * elements.
 */
const StandardLayout = ({
  children,
  noContainer,
  noHeader,
  noFooter,
  footerTopContent,
}: {
  children: React.ReactNode
  /**
   * Flag to disable automatic wrapping of children in a container that provides
   * standard horizontal spacing. Use this if you need edge-to-edge content
   * on your page. You can combine this with `StandardLayout.ContentContainer`
   * to have some elements which are edge-to-edge but others which conform
   * to the normal centered design.
   */
  noContainer?: boolean
  /**
   * Flag to disable the header.
   */
  noHeader?: boolean
  /**
   * Flag to disable the footer.
   */
  noFooter?: boolean
  /**
   * Optional content rendered immediately above the footer.
   */
  footerTopContent?: React.ReactNode
}) => {
  const logError = useLogError()

  return (
    <>
      {noHeader ? null : <Header />}

      <ErrorBoundary onError={logError}>
        <Body>
          {noContainer ? (
            children
          ) : (
            <ContentContainer>{children}</ContentContainer>
          )}
        </Body>
      </ErrorBoundary>

      {footerTopContent && (
        <ErrorBoundary onError={logError}>{footerTopContent}</ErrorBoundary>
      )}

      {noFooter ? null : (
        <FooterContainer>
          <Footer />
        </FooterContainer>
      )}
    </>
  )
}

StandardLayout.ContentContainer = ContentContainer

export default StandardLayout
